import { useEffect, useState, PropsWithChildren } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/Button/Button'
import { useSignupStore } from '@/reducers'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'
import { KycRedirect } from './KycRedirect'

type VerificationTypes = 'ID_VERIFICATION' | null

type VerificationOptionCardProps = PropsWithChildren & {
  title: string
  onSelect: () => void
}

const VerificationOptionCard = ({
  title,
  children,
  onSelect,
}: VerificationOptionCardProps) => {
  const { t } = useTranslation()

  return (
    <div className="border rounded-md border-pebble dark:border-white p-5">
      <div className="text-black dark:text-white text-base font-normal uppercase mb-4">
        {title}
      </div>
      <div className="text-black dark:text-white text-base mb-4">
        {children}
      </div>
      <Button
        type="button"
        style={{ display: 'block', width: '100%' }}
        data-testid={`btn-continue-${title.toLowerCase().replace(/\s/g, '-')}`}
        onClick={onSelect}
      >
        {t('kyc.continue')}
      </Button>
    </div>
  )
}

const IdVerificationDocumentContent = () => {
  const { t } = useTranslation()
  return (
    <div className="text-base">
      <p className="mb-2" data-testid="text-document-intro">
        {t('kyc.documentIntro')}
      </p>
      <ul className="list-disc pl-5 mb-2 space-y-1">
        <li>{t('kyc.documents.passport')}</li>
        <li>{t('kyc.documents.driversLicence')}</li>
        <li>{t('kyc.documents.provincialIdCard')}</li>
        <li>{t('kyc.documents.residencePermit')}</li>
      </ul>
      <p className="mt-4 mb-1" data-testid="text-documents-note">
        {t('kyc.documentsNote')}
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>{t('kyc.documentsNotSupported.healthCard')}</li>
      </ul>
    </div>
  )
}

export default function SignupKYCQuestions() {
  const { t } = useTranslation()
  const [verificationType, setVerificationType] =
    useState<VerificationTypes>(null)
  const isIdVerificationEnabled =
    import.meta.env.VITE_ENABLE_OLIU_ID_VERIFICATION === 'TRUE' ? true : false
  const setOliuVerifyType = useSignupStore.use.setOliuVerifyType()
  const setEmail = useSignupStore.use.setEmail()
  const [searchParams] = useSearchParams()
  const emailParams = searchParams.get('email')
  const kycStatusFailed = searchParams.get('kyc_status') === 'failed'
  if (emailParams) {
    setEmail(emailParams)
  }

  useEffect(() => {
    trackOnboardingStepViewed('kyc_method_selection')
  }, [])

  if (!verificationType) {
    return (
      <div
        className="flex flex-col gap-5"
        data-testid="text-selection-container"
      >
        <div
          className="text-lg font-normal uppercase"
          data-testid="container-title"
        >
          {t('kyc.title')}
        </div>
        {kycStatusFailed && (
          <div
            className="text-base text-black dark:text-white"
            data-testid="container-kyc-failed-message"
          >
            {t('kyc.failedMessage')}
          </div>
        )}
        <div
          className="w-full flex flex-col gap-2"
          data-testid="text-options-container"
        >
          {isIdVerificationEnabled && (
            <VerificationOptionCard
              title={t('kyc.id.title')}
              onSelect={() => {
                trackOnboardingStepCompleted('kyc_method_selection')
                setOliuVerifyType('document')
                setVerificationType('ID_VERIFICATION')
              }}
            >
              <div data-testid="container-verification-description">
                <IdVerificationDocumentContent />
              </div>
            </VerificationOptionCard>
          )}
        </div>
      </div>
    )
  }

  if (verificationType === 'ID_VERIFICATION') {
    return <KycRedirect data-testid="container-id-check-component" />
  }

  return null
}
